import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth'
import { auth } from '../config/firebase'
import { userService } from '../services/firestoreService'

interface AuthContextType {
  currentUser: User | null
  loading: boolean
  signup: (email: string, password: string, displayName?: string) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  loginWithGoogle: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updateUserProfile: (displayName: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const signup = async (email: string, password: string, displayName?: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, { displayName })
    }
  }

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password)
  }

  const logout = async () => {
    await signOut(auth)
  }

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider()
    // Add additional scopes if needed
    provider.addScope('email')
    provider.addScope('profile')
    
    // Set custom parameters
    provider.setCustomParameters({
      prompt: 'select_account'
    })
    
    await signInWithPopup(auth, provider)
  }

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email)
  }

  const updateUserProfile = async (displayName: string) => {
    if (currentUser) {
      await updateProfile(currentUser, { displayName })
    }
  }

  // Handle user profile creation/update when auth state changes
  const handleUserProfileUpdate = async (user: User) => {
    try {
      // Get existing user profile to check if they have a custom photo
      const existingProfile = await userService.getById(user.uid)
      
      // Prepare update data
      const updateData: any = {
        email: user.email || '',
        displayName: user.displayName || '',
        language: 'en',
        theme: 'system'
      }
      
      // Only update photoURL if:
      // 1. User doesn't have an existing profile, OR
      // 2. User has an existing profile but no custom photoURL, OR  
      // 3. User's existing photoURL is from Google (contains 'googleusercontent.com')
      if (!existingProfile || 
          !existingProfile.photoURL || 
          existingProfile.photoURL.includes('googleusercontent.com')) {
        updateData.photoURL = user.photoURL || undefined
      }
      // If user has a custom photoURL (uploaded via profile page), preserve it
      
      // Create or update user profile in Firestore
      await userService.createOrUpdate(user.uid, updateData)
      
      // Update last login time
      await userService.updateLastLogin(user.uid)
    } catch (error) {
      console.error('Error updating user profile:', error)
      // Don't throw error to avoid blocking authentication
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user)
      
      // Update user profile when user logs in
      if (user) {
        await handleUserProfileUpdate(user)
      }
      
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const value: AuthContextType = {
    currentUser,
    loading,
    signup,
    login,
    logout,
    loginWithGoogle,
    resetPassword,
    updateUserProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
} 